/**
 * User Mock Training Page
 * Shows assigned training scenarios and allows users to practice
 * 
 * Features:
 * - Voice-based practice using Web Speech API (STT & TTS)
 * - Text fallback for users without microphone
 * - AI-powered feedback on responses
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Brain,
  Play,
  CheckCircle,
  Clock,
  Trophy,
  Target,
  MessageSquare,
  Loader2,
  AlertTriangle,
  ChevronRight,
  Star,
  RefreshCw,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Keyboard,
  StopCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Web Speech API TTS helper
const speak = (text, onEnd, rate = 1.0) => {
  return new Promise((resolve) => {
    if (!('speechSynthesis' in window)) {
      console.warn('Web Speech API not supported');
      if (onEnd) onEnd();
      resolve();
      return;
    }
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = rate;
    utterance.pitch = 1.0;
    utterance.volume = 1;
    
    // Try to use a natural voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => 
      v.name.includes('Samantha') || 
      v.name.includes('Google US English') ||
      v.name.includes('Microsoft Zira') ||
      v.lang.startsWith('en')
    ) || voices[0];
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    utterance.onend = () => {
      if (onEnd) onEnd();
      resolve();
    };
    utterance.onerror = () => {
      if (onEnd) onEnd();
      resolve();
    };
    
    window.speechSynthesis.speak(utterance);
  });
};

// Stop speaking
const stopSpeaking = () => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
};

export default function UserMockTraining() {
  const navigate = useNavigate();
  const { user, tenant } = useAuth();
  const [activeTab, setActiveTab] = useState('assigned');
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [completedSessions, setCompletedSessions] = useState([]);
  const [stats, setStats] = useState({
    total_assigned: 0,
    completed: 0,
    average_score: 0
  });
  
  // Practice dialog
  const [showPracticeDialog, setShowPracticeDialog] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [practiceStep, setPracticeStep] = useState('intro'); // intro, practice, feedback
  const [userResponse, setUserResponse] = useState('');
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [practiceResult, setPracticeResult] = useState(null);
  
  // Voice state
  const [voiceMode, setVoiceMode] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Load voices when component mounts
  useEffect(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
    
    return () => {
      stopSpeaking();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    // Only fetch when user is available
    if (user?.id) {
      fetchData();
    }
  }, [user?.id]);

  const fetchData = async () => {
    if (!user?.id) {
      console.log('No user ID available, skipping fetch');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    console.log('Fetching mock training assignments for user:', user.id);
    
    try {
      // Fetch user's assignments
      const assignmentsRes = await api.get('/mock-training/my-assignments', {
        params: { user_id: user.id }
      });
      console.log('Assignments response:', assignmentsRes.data);
      setAssignments(assignmentsRes.data.assignments || []);
      
      // Fetch completed sessions
      try {
        const sessionsRes = await api.get('/mock-training/my-sessions', {
          params: { user_id: user.id }
        });
        setCompletedSessions(sessionsRes.data.sessions || []);
      } catch (e) {
        console.log('Sessions not available');
      }
      
      // Calculate stats
      const assigned = assignmentsRes.data.assignments || [];
      const completed = assigned.filter(a => a.status === 'completed').length;
      setStats({
        total_assigned: assigned.length,
        completed: completed,
        average_score: calculateAverageScore(assigned)
      });
      
    } catch (error) {
      console.log('Mock training data not available:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAverageScore = (assignments) => {
    const scores = assignments.filter(a => a.score).map(a => a.score);
    if (scores.length === 0) return 0;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsRecording(false);
        setIsProcessing(true);
        
        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          
          // Create form data for transcription
          const formData = new FormData();
          formData.append('audio', audioBlob, 'recording.webm');
          
          // Send to backend for transcription
          const response = await api.post('/communication-coach/transcribe', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          const transcript = response.data.text || response.data.transcript || '';
          if (transcript) {
            setUserResponse(prev => prev ? `${prev} ${transcript}` : transcript);
            toast.success('Voice captured!');
          } else {
            toast.error('Could not understand audio. Please try again.');
          }
        } catch (error) {
          console.error('Transcription error:', error);
          toast.error('Failed to process audio. Try typing instead.');
        } finally {
          setIsProcessing(false);
          // Stop all tracks
          stream.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info('Recording... Click again to stop');
    } catch (error) {
      console.error('Microphone access error:', error);
      toast.error('Could not access microphone. Please enable microphone access or use text mode.');
      setVoiceMode(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Speak the candidate's statement
  const speakCandidateStatement = async () => {
    if (!audioEnabled || !selectedScenario?.candidate_statement) return;
    
    setIsSpeaking(true);
    await speak(
      selectedScenario.candidate_statement,
      () => setIsSpeaking(false),
      0.95
    );
  };

  const startPractice = (assignment) => {
    setSelectedScenario(assignment);
    setPracticeStep('intro');
    setUserResponse('');
    setPracticeResult(null);
    setShowPracticeDialog(true);
  };

  const beginPracticeSession = async () => {
    setPracticeStep('practice');
    
    // Speak the candidate statement if audio is enabled
    if (audioEnabled && selectedScenario?.candidate_statement) {
      setTimeout(() => {
        speakCandidateStatement();
      }, 500);
    }
  };

  const submitPractice = async () => {
    if (!userResponse.trim()) {
      toast.error('Please enter or speak your response');
      return;
    }

    stopSpeaking();
    setPracticeLoading(true);
    
    try {
      const response = await api.post('/mock-training/practice', {
        assignment_id: selectedScenario.id,
        scenario_id: selectedScenario.scenario_id,
        user_response: userResponse,
        user_id: user?.id
      });
      
      setPracticeResult(response.data);
      setPracticeStep('feedback');
      
      // Speak the feedback if audio is enabled
      if (audioEnabled && response.data.feedback) {
        setTimeout(async () => {
          setIsSpeaking(true);
          const feedbackText = `Your score is ${response.data.score} percent. ${response.data.feedback}`;
          await speak(feedbackText, () => setIsSpeaking(false), 0.95);
        }, 500);
      }
      
      // Refresh data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit practice');
    } finally {
      setPracticeLoading(false);
    }
  };

  const closePracticeDialog = () => {
    stopSpeaking();
    stopRecording();
    setShowPracticeDialog(false);
  };

  const getCategoryColor = (category) => {
    const colors = {
      age: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      pregnancy: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
      religion: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      disability: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      national_origin: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      compliance: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      general: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300'
    };
    return colors[category] || colors.general;
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      easy: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
      hard: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    };
    return colors[difficulty] || colors.medium;
  };

  const getStatusBadge = (assignment) => {
    if (assignment.status === 'completed') {
      return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
    }
    if (assignment.due_date && new Date(assignment.due_date) < new Date()) {
      return <Badge variant="destructive">Overdue</Badge>;
    }
    return <Badge variant="secondary">Pending</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="user-mock-training">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-primary" />
            Mock Training
          </h1>
          <p className="text-muted-foreground">
            Practice handling challenging recruitment scenarios
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Debug Info - Shows current user ID for troubleshooting */}
      <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
        <CardContent className="p-3 text-xs text-yellow-800 dark:text-yellow-200">
          <p><strong>Debug:</strong> Your User ID: <code className="bg-yellow-100 dark:bg-yellow-800 px-1 rounded">{user?.id || 'NOT LOADED'}</code> | Email: {user?.email || 'N/A'}</p>
          <p className="mt-1 text-yellow-600 dark:text-yellow-400">This info helps verify assignments are linked to the correct user. Contact admin if assignments don't appear.</p>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Assigned</p>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.total_assigned}</p>
              </div>
              <Target className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 dark:text-green-400 font-medium">Completed</p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.completed}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            {stats.total_assigned > 0 && (
              <Progress 
                value={(stats.completed / stats.total_assigned) * 100} 
                className="mt-2 h-2"
              />
            )}
          </CardContent>
        </Card>

        <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Avg Score</p>
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                  {stats.average_score > 0 ? `${stats.average_score}%` : 'N/A'}
                </p>
              </div>
              <Trophy className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="assigned" className="gap-2">
            <Target className="h-4 w-4" />
            Assigned Training
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            Completed
          </TabsTrigger>
        </TabsList>

        {/* Assigned Training Tab */}
        <TabsContent value="assigned" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Your Training Assignments</CardTitle>
              <CardDescription>Complete these scenarios to improve your skills</CardDescription>
            </CardHeader>
            <CardContent>
              {assignments.length === 0 ? (
                <div className="text-center py-12">
                  <Target className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="font-medium text-lg">No Assignments Yet</h3>
                  <p className="text-muted-foreground mt-1">
                    Your manager will assign training scenarios to help you improve
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {assignments.filter(a => a.status !== 'completed').map((assignment) => (
                    <div
                      key={assignment.id}
                      className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                      data-testid={`assignment-${assignment.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium">{assignment.scenario_title || 'Training Scenario'}</h4>
                          {getStatusBadge(assignment)}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {assignment.scenario_description || 'Practice handling this challenging scenario'}
                        </p>
                        <div className="flex items-center gap-3 text-sm">
                          {assignment.category && (
                            <Badge className={getCategoryColor(assignment.category)}>
                              {assignment.category}
                            </Badge>
                          )}
                          {assignment.difficulty && (
                            <Badge className={getDifficultyColor(assignment.difficulty)}>
                              {assignment.difficulty}
                            </Badge>
                          )}
                          {assignment.due_date && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              Due: {new Date(assignment.due_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button onClick={() => startPractice(assignment)} data-testid={`start-btn-${assignment.id}`}>
                        <Play className="h-4 w-4 mr-2" />
                        Start Practice
                      </Button>
                    </div>
                  ))}
                  
                  {assignments.filter(a => a.status !== 'completed').length === 0 && (
                    <div className="text-center py-8">
                      <CheckCircle className="h-10 w-10 mx-auto text-green-500 mb-3" />
                      <p className="text-muted-foreground">All assignments completed! Great work!</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Completed Tab */}
        <TabsContent value="completed" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Completed Training</CardTitle>
              <CardDescription>Review your past training sessions</CardDescription>
            </CardHeader>
            <CardContent>
              {assignments.filter(a => a.status === 'completed').length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="font-medium text-lg">No Completed Training Yet</h3>
                  <p className="text-muted-foreground mt-1">
                    Complete your assigned scenarios to see them here
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {assignments.filter(a => a.status === 'completed').map((assignment) => (
                    <div
                      key={assignment.id}
                      className="flex items-start justify-between p-4 border rounded-lg bg-green-50/50 dark:bg-green-900/10"
                      data-testid={`completed-${assignment.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <h4 className="font-medium">{assignment.scenario_title || 'Training Scenario'}</h4>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {assignment.score && (
                            <span className="flex items-center gap-1">
                              <Star className="h-3 w-3 text-amber-500" />
                              Score: {assignment.score}%
                            </span>
                          )}
                          {assignment.completed_at && (
                            <span>
                              Completed: {new Date(assignment.completed_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => startPractice(assignment)}>
                        Practice Again
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Practice Dialog with Voice Support */}
      <Dialog open={showPracticeDialog} onOpenChange={closePracticeDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" />
              {selectedScenario?.scenario_title || 'Training Scenario'}
            </DialogTitle>
            <DialogDescription>
              {practiceStep === 'intro' && 'Read the scenario and prepare your response'}
              {practiceStep === 'practice' && 'Respond to the candidate using voice or text'}
              {practiceStep === 'feedback' && 'Review your feedback'}
            </DialogDescription>
          </DialogHeader>

          {/* Voice/Text Mode Toggle */}
          {practiceStep !== 'feedback' && (
            <div className="flex items-center justify-end gap-4 py-2 border-b">
              <div className="flex items-center gap-2">
                <Switch
                  id="voice-mode"
                  checked={voiceMode}
                  onCheckedChange={setVoiceMode}
                />
                <Label htmlFor="voice-mode" className="text-sm cursor-pointer flex items-center gap-1">
                  {voiceMode ? <Mic className="h-4 w-4" /> : <Keyboard className="h-4 w-4" />}
                  {voiceMode ? 'Voice Mode' : 'Text Mode'}
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  id="audio-enabled"
                  checked={audioEnabled}
                  onCheckedChange={(checked) => {
                    setAudioEnabled(checked);
                    if (!checked) stopSpeaking();
                  }}
                />
                <Label htmlFor="audio-enabled" className="text-sm cursor-pointer flex items-center gap-1">
                  {audioEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                  Audio
                </Label>
              </div>
            </div>
          )}

          <div className="py-4">
            {practiceStep === 'intro' && (
              <div className="space-y-4">
                <Card className="bg-muted/50">
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2">Scenario</h4>
                    <p className="text-sm">{selectedScenario?.scenario_description || 'A challenging recruitment scenario'}</p>
                  </CardContent>
                </Card>
                
                {selectedScenario?.candidate_statement && (
                  <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-blue-700 dark:text-blue-300">Candidate Will Say:</h4>
                        {audioEnabled && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={speakCandidateStatement}
                            disabled={isSpeaking}
                            className="text-blue-600"
                          >
                            {isSpeaking ? (
                              <StopCircle className="h-4 w-4 animate-pulse" />
                            ) : (
                              <Volume2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                      <p className="text-sm italic">"{selectedScenario.candidate_statement}"</p>
                    </CardContent>
                  </Card>
                )}
                
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <span>Remember to handle this professionally and avoid any compliance issues</span>
                </div>
              </div>
            )}

            {practiceStep === 'practice' && (
              <div className="space-y-4">
                {/* Candidate Statement */}
                <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-blue-700 dark:text-blue-300">Candidate Says:</h4>
                      {audioEnabled && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={isSpeaking ? stopSpeaking : speakCandidateStatement}
                          className="text-blue-600"
                        >
                          {isSpeaking ? (
                            <>
                              <StopCircle className="h-4 w-4 mr-1" />
                              Stop
                            </>
                          ) : (
                            <>
                              <Volume2 className="h-4 w-4 mr-1" />
                              Listen
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                    <p className="text-sm italic">"{selectedScenario?.candidate_statement || 'How would you handle this situation?'}"</p>
                  </CardContent>
                </Card>
                
                {/* Response Input */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Your Response:</h4>
                    {voiceMode && (
                      <Button
                        onClick={toggleRecording}
                        variant={isRecording ? "destructive" : "default"}
                        size="sm"
                        disabled={isProcessing}
                        className="gap-2"
                        data-testid="voice-record-btn"
                      >
                        {isProcessing ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Processing...
                          </>
                        ) : isRecording ? (
                          <>
                            <MicOff className="h-4 w-4" />
                            Stop Recording
                          </>
                        ) : (
                          <>
                            <Mic className="h-4 w-4" />
                            Start Recording
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  
                  {isRecording && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <div className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />
                      <span className="text-sm text-red-700 dark:text-red-300">Recording... Speak now</span>
                    </div>
                  )}
                  
                  <Textarea
                    value={userResponse}
                    onChange={(e) => setUserResponse(e.target.value)}
                    placeholder={voiceMode ? "Click the microphone to record your response, or type here..." : "Type how you would respond to this candidate..."}
                    rows={5}
                    className="resize-none"
                    data-testid="practice-response-input"
                  />
                  
                  {voiceMode && (
                    <p className="text-xs text-muted-foreground">
                      Tip: Click the microphone button and speak your response. Your speech will be transcribed automatically.
                    </p>
                  )}
                </div>
              </div>
            )}

            {practiceStep === 'feedback' && practiceResult && (
              <div className="space-y-4">
                <Card className={cn(
                  "border-2",
                  practiceResult.score >= 80 ? "border-green-500 bg-green-50 dark:bg-green-900/20" :
                  practiceResult.score >= 60 ? "border-amber-500 bg-amber-50 dark:bg-amber-900/20" :
                  "border-red-500 bg-red-50 dark:bg-red-900/20"
                )}>
                  <CardContent className="p-4 text-center">
                    <div className="text-4xl font-bold mb-2">
                      {practiceResult.score}%
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {practiceResult.score >= 80 ? 'Excellent work!' :
                       practiceResult.score >= 60 ? 'Good effort, room for improvement' :
                       'Needs more practice'}
                    </p>
                    {isSpeaking && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={stopSpeaking}
                        className="mt-2"
                      >
                        <VolumeX className="h-4 w-4 mr-1" />
                        Stop Audio
                      </Button>
                    )}
                  </CardContent>
                </Card>

                {practiceResult.feedback && (
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="font-medium mb-2">Feedback</h4>
                      <p className="text-sm">{practiceResult.feedback}</p>
                    </CardContent>
                  </Card>
                )}

                {selectedScenario?.ideal_response && (
                  <Card className="bg-green-50 dark:bg-green-900/20 border-green-200">
                    <CardContent className="p-4">
                      <h4 className="font-medium mb-2 text-green-700 dark:text-green-300">Ideal Response:</h4>
                      <p className="text-sm">{selectedScenario.ideal_response}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            {practiceStep === 'intro' && (
              <>
                <Button variant="outline" onClick={closePracticeDialog}>
                  Cancel
                </Button>
                <Button onClick={beginPracticeSession}>
                  <ChevronRight className="h-4 w-4 mr-2" />
                  Start Practice
                </Button>
              </>
            )}

            {practiceStep === 'practice' && (
              <>
                <Button variant="outline" onClick={() => setPracticeStep('intro')}>
                  Back
                </Button>
                <Button 
                  onClick={submitPractice} 
                  disabled={practiceLoading || !userResponse.trim() || isRecording}
                >
                  {practiceLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Submit Response
                    </>
                  )}
                </Button>
              </>
            )}

            {practiceStep === 'feedback' && (
              <>
                <Button variant="outline" onClick={closePracticeDialog}>
                  Close
                </Button>
                <Button onClick={() => {
                  setPracticeStep('intro');
                  setUserResponse('');
                  setPracticeResult(null);
                }}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Practice Again
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
